import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import http from "../../utils/http";
import { DateTime } from "luxon";
import StatusChangeConfirmDialog from "./StatusChangeConfirmDialog";
import { timezoneLabel } from "../../utils/timezone";
import { Slot } from "../../Redux/reducers/appointmentSlice";
import { mapSlotInviteesToDetails } from "../Appointments/appointmentParticipantUtils";
import AppointmentParticipantsSection from "../Appointments/AppointmentParticipantsSection";

type Props = {
  showModal: boolean;
  closeModal: () => void;
  data?: Slot;
  roomId: string;
  slotId?: string;
  refreshPage: () => void;
  handleReschedule?: (slot: any) => void;
};

const ViewAppointmentModal = ({
  showModal,
  closeModal,
  data,
  roomId,
  slotId,
  refreshPage,
  handleReschedule,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [slotData, setSlotData] = useState(data);
  const [selectedStatus, setSelectedStatus] = useState<
    "completed" | "no_show" | "cancelled" | ""
  >("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Check if appointment is in the past
  const isPastAppointment = useMemo(() => {
    if (!slotData?.startDateTime) return false;
    const appointmentDate = DateTime.fromJSDate(slotData.startDateTime);
    const now = DateTime.now();
    return appointmentDate < now;
  }, [slotData]);

  const getStatusLabel = (
    status: "completed" | "no_show" | "cancelled" | "",
  ) => {
    switch (status) {
      case "completed":
        return "Mark as Complete";
      case "no_show":
        return "Mark as No-Show";
      case "cancelled":
        return "Cancel Appointment";
      default:
        return "";
    }
  };

  const handleStatusChange = async () => {
    if (!selectedStatus || !slotData?.id) return;
    try {
      setSubmitLoading(true);
      if (selectedStatus === "cancelled") {
        const res = await http.post(
          `/admin/registry/appointment-rooms/${roomId}/slots/${slotData?.id}/cancel`,
        );
        toastMessage("success", res.data.message);
      } else {
        const res = await http.patch(
          `/admin/registry/appointments/${slotData?.appointmentId}/status?studyId=${slotData?.appointment?.studyId}`,
          {
            status: selectedStatus,
          },
        );
        toastMessage("success", res.data.message);
      }
      refreshPage();
      closeModal();
      setConfirmOpen(false);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedStatus) {
      toastMessage("error", "Please select a status to update.");
      return;
    }
    setConfirmOpen(true);
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        let url = `/admin/registry/appointment-rooms/${roomId}/slots/${slotId}`;
        const res = await http.get(url);
        const slot = res?.data?.data;
        const apt = slot?.appointment;

        const siteTimezone =
          slot?.room?.site?.timezone || DateTime.local().zoneName;

        const startInSiteTz = DateTime.fromISO(slot?.startDateTime, {
          zone: siteTimezone,
        });
        const endInSiteTz = DateTime.fromISO(slot?.endDateTime, {
          zone: siteTimezone,
        });

        const formattedSlot: Slot = {
          id: slot?.id,
          appointmentId: slot?.appointmentId,
          roomId: slot?.roomId,
          startDateTime: new Date(slot?.startDateTime),
          endDateTime: new Date(slot?.endDateTime),
          status: slot?.status,
          date: DateTime.fromISO(slot?.startDateTime).toFormat("MM/dd/yyyy"),
          time:
            DateTime.fromISO(slot?.startDateTime).toFormat("hh:mm a") +
            " - " +
            DateTime.fromISO(slot?.endDateTime).toFormat("hh:mm a"),
          timezone: siteTimezone,
          timeZoneLabel: timezoneLabel(siteTimezone),
          roomDate: startInSiteTz.toFormat("MM/dd/yyyy"),
          roomTime:
            startInSiteTz.toFormat("hh:mm a") +
            " - " +
            endInSiteTz.toFormat("hh:mm a"),
          appointment: apt
            ? {
                id: apt.id,
                study: apt?.study?.name,
                studyId: apt?.studyId,
                status: apt.status,
                inviters: apt.inviters?.map((inviter: any) => ({
                  id: inviter?.inviter?.id,
                  name: inviter?.inviter?.firstName
                    ? inviter?.inviter?.lastName
                      ? `${inviter?.inviter?.firstName} ${inviter?.inviter?.lastName}`
                      : inviter?.inviter?.firstName
                    : "",
                })),
                invitees: mapSlotInviteesToDetails(apt.invitees),
                notes: apt.notes,
              }
            : undefined,
        };
        setSlotData(formattedSlot);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    if (slotId && roomId) {
      fetchDetails();
    } else {
      setLoading(false);
    }
  }, [roomId, slotId]);

  return (
    <>
      <Modal open={showModal} onClose={closeModal}>
        <Box sx={ModalDrawerStyles}>
          <ModalHeader title="View Appointment" onCloseClick={closeModal} />

          {loading ? (
            <Stack
              sx={{
                flex: 1,
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress color="primary" />
            </Stack>
          ) : (
            <Box sx={{ flex: 1 }}>
              {/* Date & Time */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Date & Time (Local)
                </Typography>
                <Typography variant="body1">
                  {slotData?.date} {slotData?.time}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Date & Time ({slotData?.timeZoneLabel})
                </Typography>
                <Typography variant="body1">
                  {slotData?.roomDate} {slotData?.roomTime}
                </Typography>
              </Box>

              {/* Study */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Study
                </Typography>
                <Typography variant="body1">
                  {slotData?.appointment?.study || "Not assigned"}
                </Typography>
              </Box>

              <AppointmentParticipantsSection
                participants={slotData?.appointment?.invitees || []}
              />

              {/* Internal Team */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Internal Team
                </Typography>
                {slotData?.appointment?.inviters &&
                slotData.appointment.inviters.length > 0 ? (
                  <Box>
                    {slotData.appointment.inviters.map((inviter, index) => (
                      <Typography key={inviter.id} variant="body1">
                        {index + 1}. {inviter.name || "Unknown"}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No team members
                  </Typography>
                )}
              </Box>

              {/* Notes */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Notes
                </Typography>
                <Typography
                  variant="body1"
                  color={
                    slotData?.appointment?.notes
                      ? "text.primary"
                      : "text.secondary"
                  }
                >
                  {slotData?.appointment?.notes || "No notes added"}
                </Typography>
              </Box>

              {slotData?.appointment?.status === "scheduled" && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 3 }} />
                  <FormControl component="fieldset">
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Change Status
                    </Typography>
                    <RadioGroup
                      value={selectedStatus}
                      onChange={(event) =>
                        setSelectedStatus(
                          event.target.value as typeof selectedStatus,
                        )
                      }
                    >
                      <FormControlLabel
                        value="completed"
                        control={<Radio />}
                        label="Mark as Complete"
                      />
                      <FormControlLabel
                        value="no_show"
                        control={<Radio />}
                        label="Mark as No-Show"
                      />
                      <FormControlLabel
                        value="cancelled"
                        control={<Radio />}
                        label="Cancel Appointment"
                        disabled={isPastAppointment}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}

          <Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={ModalDrawerButtonStyles}>
              {slotData?.appointment?.status === "scheduled" && (
                <Button
                  variant="contained"
                  onClick={() => handleReschedule?.(slotData)}
                  disabled={isPastAppointment}
                >
                  Reschedule
                </Button>
              )}
              <Button variant="outlined" onClick={closeModal}>
                Close
              </Button>
              {slotData?.appointment?.status === "scheduled" && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={submitLoading || !selectedStatus}
                >
                  {submitLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Submit"
                  )}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
      {confirmOpen && (
        <StatusChangeConfirmDialog
          open={confirmOpen}
          statusLabel={getStatusLabel(selectedStatus)}
          statusKey={selectedStatus}
          onClose={() => {
            if (!submitLoading) {
              setConfirmOpen(false);
            }
          }}
          onConfirm={handleStatusChange}
          confirmLoading={submitLoading}
        />
      )}
    </>
  );
};

export default ViewAppointmentModal;
